import React from 'react';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';
import {Plugins, Component, withSystem, Atom} from '@packages/systems/wfdl';
import type {AtomRenderProps} from '@packages/systems/wfdl';
import {getChildren, getInPojo} from '@packages/utilities/data-utils';
import {
  TBoolean,
  TEnum,
  TListOfElement,
  TRecord,
  TText,
  EElement,
  EText,
  ERecord,
  EList,
  EEnum,
  ELiteral,
  EBoolean,
} from '@packages/systems/wfdl-ast';
import {px, percent} from '@packages/systems/core/utils/WebflowCssSubset';
import {createRgbaColor} from '@packages/systems/core/utils/ColorUtils';
import {getSecond} from '@packages/utilities/Pair';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {generateGuid} from '@packages/utilities/guid';
import {createDevlinkAtom} from '@packages/utilities/integrated-types';

const TEMP_STATE_PATH = ['temp', 'state'];

const PlayPauseButton = Atom.createRender(
  ({
    attributes,
    classes,
    data,
    generateAttributes,
    renderChild,
    context,
  }: AtomRenderProps) => {
    const updatedClasses = classes.push('w-background-video--control');
    const state = getInPojo(data, TEMP_STATE_PATH);
    const updatedContext = context.set('state', state);
    const updatedAttributes = attributes
      .set('aria-controls', context.get('videoId', ''))
      .set('type', 'button')
      .set('data-w-bg-video-control', 'true');
    const posterUrl = context.get('posterUrl', '');
    const __html = `
  [data-wf-bgvideo-fallback-img] {
    display: none;
  }
  @media (prefers-reduced-motion: reduce) {
    [data-wf-bgvideo-fallback-img] {
      position: absolute;
      z-index: -100;
      display: inline-block;
      height: 100%;
      width: 100%;
      object-fit: cover;
    }
  }`;

    return (
      <>
        <noscript>
          {/* eslint-disable-next-line react/no-danger */}
          <style dangerouslySetInnerHTML={{__html}} />
          <img data-wf-bgvideo-fallback-img src={posterUrl} alt="" />
        </noscript>
        <div aria-live="polite">
          <button {...generateAttributes(updatedAttributes, updatedClasses)}>
            {getChildren(data).map((child) =>
              renderChild(child, updatedContext)
            )}
          </button>
        </div>
      </>
    );
  }
);

const config = createDevlinkAtom({
  displayName: 'Play / pause button',
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Basic'), iconName('Button')),
  },
  showEmpty: true,
  constraints: {
    wraps: [
      'BackgroundVideoPlayPauseButtonPlaying',
      'BackgroundVideoPlayPauseButtonPaused',
    ],
    children: [
      {is: 'BackgroundVideoPlayPauseButtonPlaying', rule: 'ExactlyOne'},
      {is: 'BackgroundVideoPlayPauseButtonPaused', rule: 'ExactlyOne'},
    ],
    ancestors: [{is: 'BackgroundVideoWrapper', rule: 'ExactlyOne'}],
  },
  render: PlayPauseButton,
  fields: {
    children: TListOfElement,
    attr: withSystem(
      {private: true},
      TRecord({
        type: withSystem({defaultValue: 'button'}, TText),
        'data-w-bg-video-control': withSystem({defaultValue: true}, TBoolean),
        'aria-controls': TText,
      })
    ),
    temp: TRecord({
      state: withSystem(
        {
          label: 'State',
          defaultValue: 'playing',
        },
        TEnum({playing: 'Play', paused: 'Pause'})
      ),
    }),
  },
  style: {
    base: {
      main: {
        'focus-visible': {
          borderBottomLeftRadius: percent(50),
          borderBottomRightRadius: percent(50),
          borderTopLeftRadius: percent(50),
          borderTopRightRadius: percent(50),
          outlineStyle: 'solid',
          outlineWidth: px(2),
          outlineOffset: px(2),
          outlineColor: createRgbaColor('#3b79c3'),
        },
      },
    },
  },
});

export default config;

export const DEFAULT_PLAY_PAUSE_BUTTON = getSecond(
  instantiateExpressionWithUniqueIds(
    EElement({
      id: 'Play / pause button',
      type: qualify(
        Plugins.name('BackgroundVideo'),
        Component.name('BackgroundVideoPlayPauseButton')
      ),
      data: ERecord({
        temp: ERecord({
          state: EEnum('playing'),
        }),
        children: EList([
          EElement({
            id: 'Play state',
            type: qualify(
              Plugins.name('BackgroundVideo'),
              Component.name('BackgroundVideoPlayPauseButtonPlaying')
            ),
            data: ERecord({
              children: EList([
                EElement({
                  id: 'Pause Icon Image',
                  type: qualify(Plugins.name('Basic'), Component.name('Image')),
                  data: ERecord({
                    img: ELiteral({
                      name: qualify(Plugins.name('Asset'), typeName('Image')),
                      value: {},
                    }),
                    attr: ERecord({
                      src: EText(
                        'https://uploads-ssl.webflow.com/6022af993a6b2191db3ed10c/628299f8aa233b83918e24fd_Pause.svg'
                      ),
                      loading: EEnum('lazy'),
                      width: EText('auto'),
                      height: EText('auto'),
                      alt: EText('Pause video'),
                    }),
                    temp: ERecord({
                      img: ERecord({
                        src: EText(''),
                      }),
                    }),
                    srcsetDisabled: EBoolean(false),
                    sizes: EList([]),
                  }),
                }),
              ]),
            }),
          }),
          EElement({
            id: 'Pause state',
            type: qualify(
              Plugins.name('BackgroundVideo'),
              Component.name('BackgroundVideoPlayPauseButtonPaused')
            ),
            data: ERecord({
              children: EList([
                EElement({
                  id: 'Play Icon Image',
                  type: qualify(Plugins.name('Basic'), Component.name('Image')),
                  data: ERecord({
                    img: ELiteral({
                      name: qualify(Plugins.name('Asset'), typeName('Image')),
                      value: {},
                    }),
                    attr: ERecord({
                      loading: EEnum('lazy'),
                      width: EText('auto'),
                      height: EText('auto'),
                      src: EText(
                        'https://uploads-ssl.webflow.com/6022af993a6b2191db3ed10c/628298b20ae0236682d4b87f_Play-24.svg'
                      ),
                      alt: EText('Play video'),
                    }),
                    temp: ERecord({
                      img: ERecord({
                        src: EText(''),
                      }),
                    }),
                    srcsetDisabled: EBoolean(false),
                    sizes: EList([]),
                  }),
                }),
              ]),
            }),
          }),
        ]),
      }),
    })
  )(generateGuid())
);
